/**
 * Clientes de los servicios REST JSON publicos de la Sede del Catastro (OVC).
 * Sin clave, CORS `*` (comprobado 2026-09-22). Estructuras tomadas de respuestas reales
 * (fixtures en `tests/fixtures/dnprc_*.json`):
 *
 * - Consulta_RCCOOR: `Consulta_RCCOORResult.coordenadas.coord[0].pc.{pc1,pc2}` y `ldt` (direccion);
 *   error en `control.cuerr` + `lerr[]` ("PARA ESAS COORDENADAS NO HAY REFERENCIA DISPONIBLE").
 * - Consulta_DNPRC (parametro `RefCat`): `consulta_dnprcResult.bico.bi` cuando la parcela no tiene
 *   division horizontal (un unico bien, con `finca` y `lcons[]` = locales), o
 *   `consulta_dnprcResult.lrcdnp.rcdnp[]` cuando hay varios inmuebles (pisos). En ambos, cada
 *   inmueble trae `rc.{pc1,pc2,car,cc1,cc2}`, `dt.locs.lous.lourb.{dir,loint.{es,pt,pu,bq},dp}`,
 *   `ldt` (a veces) y `debi.{luso,sfc,cpt,ant}` con los numeros como texto.
 */

export interface InmuebleOvc {
  rc20: string;
  direccion: string | null;
  escalera: string | null;
  planta: string | null;
  puerta: string | null;
  bloque: string | null;
  superficieM2: number | null;
  uso: string | null;
  anio: number | null;
  /** Participacion en la finca (porcentaje), si viene. */
  participacion: number | null;
}

export interface FincaOvc {
  direccion: string | null;
  tipo: string | null;
  superficieSueloM2: number | null;
  /** Locales o construcciones de la finca (solo sin division horizontal). */
  locales: { uso: string | null; planta: string | null; puerta: string | null; superficieM2: number | null; tipo: string | null }[];
}

export interface RespuestaInmuebles {
  inmuebles: InmuebleOvc[];
  finca: FincaOvc | null;
}

// Tipos parciales del JSON del Catastro. Todo opcional: el servicio omite lo que no aplica.
interface Lerr { cod?: string; des?: string }
interface Rc { pc1?: string; pc2?: string; car?: string; cc1?: string; cc2?: string }
interface Loint { es?: string; pt?: string; pu?: string; bq?: string }
interface Dir { tv?: string; nv?: string; pnp?: string; snp?: string }
interface Lourb { dir?: Dir; loint?: Loint; dp?: string }
interface Dt { np?: string; nm?: string; locs?: { lous?: { lourb?: Lourb } } }
interface Debi { luso?: string; sfc?: string | number; cpt?: string; ant?: string | number }
interface Bi { idbi?: { rc?: Rc }; dt?: Dt; ldt?: string; debi?: Debi }
interface Lcon { lcd?: string; dt?: { lourb?: { loint?: Loint } }; dfcons?: { stl?: string | number }; dvcons?: { dtip?: string } }
interface Finca { ldt?: string; ltp?: string; dff?: { ss?: string | number } }
interface Rcdnp { rc?: Rc; dt?: Dt; ldt?: string; debi?: Debi }
interface ResultadoDnprc {
  consulta_dnprcResult?: {
    control?: { cuerr?: number; cudnp?: number };
    lerr?: Lerr[];
    bico?: { bi?: Bi; finca?: Finca; lcons?: Lcon[] };
    lrcdnp?: { rcdnp?: Rcdnp[] };
  };
}
interface ResultadoRccoor {
  Consulta_RCCOORResult?: {
    control?: { cuerr?: number; cucoor?: number };
    lerr?: Lerr[];
    coordenadas?: { coord?: { pc?: Rc; ldt?: string }[] };
  };
}

const BASE_CALLEJERO = 'https://ovc.catastro.meh.es/OVCServWeb/OVCWcfCallejero';

function numero(v: string | number | undefined): number | null {
  if (v === undefined || v === null || v === '') return null;
  const n = typeof v === 'number' ? v : Number(String(v).replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

function texto(v: string | undefined): string | null {
  return v === undefined || v === null || v === '' ? null : String(v);
}

function rc20De(rc: Rc | undefined): string | null {
  if (!rc || !rc.pc1 || !rc.pc2) return null;
  return `${rc.pc1}${rc.pc2}${rc.car ?? ''}${rc.cc1 ?? ''}${rc.cc2 ?? ''}`.toUpperCase();
}

function direccionDe(dt: Dt | undefined, ldt: string | undefined): string | null {
  if (ldt) return ldt.replace(/\s+/g, ' ').trim();
  const d = dt?.locs?.lous?.lourb?.dir;
  if (!d) return null;
  const partes = [d.tv, d.nv, d.pnp].filter((s) => s && s !== '0');
  const municipio = dt?.nm ? ` ${dt.nm}` : '';
  return partes.length ? `${partes.join(' ')}${municipio}`.trim() : null;
}

function inmuebleDe(rc: Rc | undefined, dt: Dt | undefined, ldt: string | undefined, debi: Debi | undefined): InmuebleOvc | null {
  const rc20 = rc20De(rc);
  if (!rc20) return null;
  const loint = dt?.locs?.lous?.lourb?.loint;
  return {
    rc20,
    direccion: direccionDe(dt, ldt),
    escalera: texto(loint?.es),
    planta: texto(loint?.pt),
    puerta: texto(loint?.pu),
    bloque: texto(loint?.bq),
    superficieM2: numero(debi?.sfc),
    uso: texto(debi?.luso),
    anio: numero(debi?.ant),
    participacion: numero(debi?.cpt),
  };
}

function mensajeError(lerr: Lerr[] | undefined): string | null {
  if (!lerr || lerr.length === 0) return null;
  return lerr.map((e) => e.des ?? e.cod ?? 'error').join('; ');
}

export class ErrorCatastro extends Error {
  constructor(mensaje: string, public readonly codigo?: string) {
    super(mensaje);
    this.name = 'ErrorCatastro';
  }
}

/**
 * Referencia catastral de la parcela que contiene un punto. Devuelve null cuando el Catastro
 * responde que no hay referencia (calles, dominio publico) y lanza en errores de red o formato.
 */
export async function consultarRcPorCoordenadas(
  x: number, y: number, epsg: number, f: typeof fetch = fetch,
): Promise<{ rc: string; direccion: string | null } | null> {
  const url = `${BASE_CALLEJERO}/COVCCoordenadas.svc/json/Consulta_RCCOOR?SRS=EPSG:${epsg}&CoorX=${x}&CoorY=${y}`;
  const resp = await f(url);
  if (!resp.ok) throw new ErrorCatastro(`Consulta_RCCOOR: HTTP ${resp.status}`);
  const datos = (await resp.json()) as ResultadoRccoor;
  const r = datos.Consulta_RCCOORResult;
  if (!r) throw new ErrorCatastro('Consulta_RCCOOR: respuesta sin resultado');
  const coord = r.coordenadas?.coord?.[0];
  if (!coord?.pc?.pc1 || !coord.pc.pc2) {
    const err = mensajeError(r.lerr);
    if (err && !/NO HAY REFERENCIA/i.test(err)) throw new ErrorCatastro(err, r.lerr?.[0]?.cod);
    return null;
  }
  return { rc: `${coord.pc.pc1}${coord.pc.pc2}`.toUpperCase(), direccion: texto(coord.ldt) };
}

/**
 * Inmuebles (bienes) de una referencia de 14 caracteres. Para una parcela sin division horizontal
 * devuelve un unico inmueble mas la finca con sus locales; para un edificio de pisos, la lista.
 */
export async function consultarInmuebles(rc14: string, f: typeof fetch = fetch): Promise<RespuestaInmuebles> {
  const rc = rc14.replace(/\s+/g, '').toUpperCase();
  const url = `${BASE_CALLEJERO}/COVCCallejero.svc/json/Consulta_DNPRC?Provincia=&Municipio=&RefCat=${encodeURIComponent(rc)}`;
  const resp = await f(url);
  if (!resp.ok) throw new ErrorCatastro(`Consulta_DNPRC: HTTP ${resp.status}`);
  const datos = (await resp.json()) as ResultadoDnprc;
  const r = datos.consulta_dnprcResult;
  if (!r) throw new ErrorCatastro('Consulta_DNPRC: respuesta sin resultado');
  const err = mensajeError(r.lerr);
  if (err) throw new ErrorCatastro(err, r.lerr?.[0]?.cod);

  const inmuebles: InmuebleOvc[] = [];
  let finca: FincaOvc | null = null;

  if (r.bico?.bi) {
    const bi = r.bico.bi;
    const i = inmuebleDe(bi.idbi?.rc, bi.dt, bi.ldt, bi.debi);
    if (i) inmuebles.push(i);
    finca = {
      direccion: texto(r.bico.finca?.ldt) ?? i?.direccion ?? null,
      tipo: texto(r.bico.finca?.ltp),
      superficieSueloM2: numero(r.bico.finca?.dff?.ss),
      locales: (r.bico.lcons ?? []).map((l) => ({
        uso: texto(l.lcd),
        planta: texto(l.dt?.lourb?.loint?.pt),
        puerta: texto(l.dt?.lourb?.loint?.pu),
        superficieM2: numero(l.dfcons?.stl),
        tipo: texto(l.dvcons?.dtip),
      })),
    };
  }
  for (const x of r.lrcdnp?.rcdnp ?? []) {
    const i = inmuebleDe(x.rc, x.dt, x.ldt, x.debi);
    if (i) inmuebles.push(i);
  }
  return { inmuebles, finca };
}

export function urlFotoFachada(rc14: string): string {
  // El WFS enlaza la version http; la https responde igual (comprobado 2026-09-22) y evita contenido mixto.
  return `https://ovc.catastro.meh.es/OVCServWeb/OVCWcfLibres/OVCFotoFachada.svc/RecuperarFotoFachadaGet?ReferenciaCatastral=${rc14.toUpperCase()}`;
}

export function urlSedeCatastro(rc14: string): string {
  const rc = rc14.toUpperCase();
  return `https://www1.sedecatastro.gob.es/CYCBienInmueble/OVCListaBienes.aspx?rc1=${rc.slice(0, 7)}&rc2=${rc.slice(7, 14)}`;
}

/** Reconoce una referencia catastral de 14 o 20 caracteres (admite espacios intermedios). */
export function esReferenciaCatastral(texto: string): { rc14: string; rc20?: string } | null {
  const limpio = texto.replace(/\s+/g, '').toUpperCase();
  if (!/^[A-Z0-9]+$/.test(limpio)) return null;
  if (limpio.length === 14) return { rc14: limpio };
  if (limpio.length === 20) return { rc14: limpio.slice(0, 14), rc20: limpio };
  return null;
}

/** Planta legible: "OD"/"OS" (unico), "-1" (sotano), "00" (bajo), "EN" (entresuelo), "01".. */
export function nombrePlanta(planta: string | null): string {
  if (!planta) return '';
  switch (planta.toUpperCase()) {
    case 'OD': case 'OS': return 'Todo el edificio';
    case '00': return 'Bajo';
    case 'EN': return 'Entresuelo';
    case 'PB': return 'Bajo';
    case 'AT': return 'Ático';
    case 'SM': return 'Semisótano';
    default: {
      const n = Number(planta);
      if (Number.isFinite(n)) return n < 0 ? `Sótano ${-n}` : `Planta ${n}`;
      return planta;
    }
  }
}
